import { ApiError, isApiError } from '@/api/errors';
import { PROBLEM_CATALOGUE } from '@/api/generated-error-catalogue';
import {
  ERROR_CATEGORY_CATALOGUE,
  type ExtractionErrorCategoryMetadata,
} from '@/api/generated-error-categories';
import { ExtractionErrorCategory, ProblemSlug } from '@/api/generated-types';

const KNOWN_SLUGS = new Set<string>(Object.values(ProblemSlug));
const warnedUnknownSlugs = new Set<string>();

function messageForCode(code: string): string | undefined {
  if (KNOWN_SLUGS.has(code)) {
    return PROBLEM_CATALOGUE[code as ProblemSlug].userMessage;
  }
  if (!warnedUnknownSlugs.has(code)) {
    warnedUnknownSlugs.add(code);
    console.warn(
      `[apiErrors] Unknown ProblemSlug "${code}" returned by backend. ` +
        `Frontend catalogue is out of date — run \`pnpm tl\`.`
    );
  }
  return undefined;
}

const KNOWN_CATEGORIES = new Set<string>(Object.values(ExtractionErrorCategory));
const warnedUnknownCategories = new Set<string>();

export function categoryMetadata(
  category: string | null | undefined
): ExtractionErrorCategoryMetadata | undefined {
  if (!category) return undefined;
  if (KNOWN_CATEGORIES.has(category)) {
    return ERROR_CATEGORY_CATALOGUE[category as ExtractionErrorCategory];
  }
  if (!warnedUnknownCategories.has(category)) {
    warnedUnknownCategories.add(category);
    console.warn(
      `[apiErrors] Unknown ExtractionErrorCategory "${category}" returned by backend. ` +
        `Frontend catalogue is out of date — run \`pnpm tl\`.`
    );
  }
  return undefined;
}

export const isRetryable = (category: string | null | undefined): boolean =>
  !!categoryMetadata(category)?.retryable;

export const isReportable = (category: string | null | undefined): boolean =>
  !!categoryMetadata(category)?.reportable;

export interface ValidationError {
  message: string;
  field: string;
  property?: string;
  cargoId?: string;
  rowIndex?: number | null;
}

// A raw exception detail or an unbounded string would otherwise grow a toast
// until it fills the screen (toasts cap width, not height). Keeps it readable.
const MAX_MESSAGE_LENGTH = 400;

function clamp(message: string): string {
  return message.length > MAX_MESSAGE_LENGTH
    ? `${message.slice(0, MAX_MESSAGE_LENGTH).trimEnd()}…`
    : message;
}

/**
 * User-facing message for any error thrown by an api call. Reads the
 * problem+json contract (`ApiError`) first; falls back to detail/title/default
 * for unknown slugs or non-problem errors. Forward-compat: an older frontend
 * meeting a newer backend slug degrades through this chain rather than
 * showing the generic default for everything.
 */
export function getErrorMessage(err: unknown, defaultMessage: string): string {
  if (!isApiError(err)) {
    return err instanceof Error && err.message
      ? clamp(`${defaultMessage}: ${err.message}`)
      : defaultMessage;
  }

  if (err.errors && Object.keys(err.errors).length > 0) {
    const lines = Object.entries(err.errors)
      .slice(0, 5)
      .map(([field, messages]) => `${field}: ${messages.join(', ')}`);
    const remaining = Object.keys(err.errors).length - lines.length;
    const suffix = remaining > 0 ? `\n... and ${remaining} more` : '';
    return clamp(`${defaultMessage}:\n${lines.join('\n')}${suffix}`);
  }

  const friendly = messageForCode(err.code);
  if (friendly) {
    // A 5xx detail is a server-side exception dump, not user-facing copy — show
    // the curated message only (the trace id + console carry the diagnostics).
    // 4xx details (validation hints, conflicts) are worth appending.
    const showDetail = err.status < 500 && err.detail && err.detail !== friendly;
    return showDetail ? clamp(`${friendly} ${err.detail}`) : friendly;
  }

  // Unknown slug: never dump a 5xx exception body into the UI.
  if (err.status >= 500) return defaultMessage;
  return clamp(err.detail ?? defaultMessage);
}

export function isNotFoundError(error: unknown): boolean {
  return isApiError(error) && error.status === 404;
}

/**
 * Flattens a problem+json validation document into the per-field shape the
 * submission-error UI was built around. Used by hooks that need to highlight
 * individual form fields rather than render a single toast.
 */
export function toValidationErrors(error: ApiError): ValidationError[] {
  if (!error.errors) return [];
  const cargoIndices = error.cargoIndices ?? {};
  return Object.entries(error.errors).flatMap(([property, messages]) =>
    messages.map((message) => ({
      message,
      field: property,
      property,
      cargoId: undefined,
      rowIndex: cargoIndices[property] ?? null,
    }))
  );
}

export function hasFieldLevelErrors(error: unknown): error is ApiError {
  return isApiError(error) && !!error.errors && Object.keys(error.errors).length > 0;
}

export function logError(context: string, error: unknown): void {
  console.error(`[${context}] Error:`, error);
}
